/**
 * Scheduled Screenshot Watcher — Helpers
 *
 * Perceptual hash comparison utilities used by the watcher coordinator
 * to determine whether the current screen matches the reference image.
 */

/**
 * Compute hamming distance between two hex-encoded perceptual hashes.
 * Both hashes must be the same length (typically 16 hex chars = 64 bits).
 * Returns the number of differing bits.
 */
export function hammingDistance(hashA: string, hashB: string): number {
  if (hashA.length !== hashB.length) {
    throw new Error(
      `Hash length mismatch: ${hashA.length} vs ${hashB.length}`,
    );
  }

  let distance = 0;
  for (let i = 0; i < hashA.length; i++) {
    const nibbleA = parseInt(hashA[i], 16);
    const nibbleB = parseInt(hashB[i], 16);
    let xor = nibbleA ^ nibbleB;
    while (xor > 0) {
      distance += xor & 1;
      xor >>= 1;
    }
  }
  return distance;
}

/**
 * Check if two hashes are within the specified hamming distance tolerance.
 */
export function isHashWithinTolerance(
  actualHash: string,
  referenceHash: string,
  tolerance: number,
): boolean {
  return hammingDistance(actualHash, referenceHash) <= tolerance;
}
